/*
 * NavbarContainer.jsx
 * Created by michaelbray on 10/3/17
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { throttle, debounce } from 'lodash';

import { CART_PROPS, ACCOUNT_PROPS, LOGIN_NOTICE_PROPS, GUEST_LOGIN_ALLOWED_PATH } from '../../components/navigation/constants';

import SearchInputContainer from './SearchInputContainer';
import AlertsNotificationContainer from './AlertsNotificationContainer';

import MobileAppNavbar from './MobileAppNavbar';
import MobileMenuWrapper from '../../components/navigation/mobileMenu/MobileMenuWrapper';

import { SkipNavLink } from '../../components/sharedComponents/SkipNav';
import AccountPanel from '../../components/navigation/account/AccountPanel';
import Button from '../../components/sharedComponents/Button';

import * as recaptchav3Lib from '../../components/sharedComponents/recaptcha/recaptchav3Lib';
import * as Icons from '../../components/sharedComponents/icons/Icons';
import * as BrandingIcons from '../../components/sharedComponents/icons/branding/BrandingIcons';

import * as cartActions from '../../actions/navigation/cartActions';
import * as loginActions from '../../actions/navigation/loginActions';

import * as securityHelper from '../../helpers/securityHelper';
import AndroidAppDownloadBanner from "../../components/AndroidAppDownloadBanner";

const combinedActions = Object.assign({},
    cartActions,
    loginActions
);

const propTypes = {
    cart: CART_PROPS,
    auth: PropTypes.shape({
        account: ACCOUNT_PROPS,
        loginNotice: LOGIN_NOTICE_PROPS,
        loginError: PropTypes.bool,
        lockout: PropTypes.bool,
        mfaRequired: PropTypes.bool,
        accountServiceEnabled: PropTypes.bool,
        isGuest: PropTypes.bool
    }),
    modal: PropTypes.shape({
        showLoginModal: PropTypes.bool,
        showSignInGuestModal: PropTypes.bool,
        showCreateAccountModal: PropTypes.bool,
        clearShowCreateAccountModal: PropTypes.func,
        showResetModal: PropTypes.bool,
        clearShowResetModal: PropTypes.func
    }),
    fetchCart: PropTypes.func,
    guestLogin: PropTypes.func,
    login: PropTypes.func,
    loggedIn: PropTypes.func,
    logout: PropTypes.func,
    clearShowLoginModal: PropTypes.func,
    clearShowSignInGuestModal: PropTypes.func,
    updateAccountServiceStatus: PropTypes.func,
    extendTimer: PropTypes.func,
    verification: PropTypes.string
};

const NAVBAR_SEARCH_INPUT_ID = 'navbar-search-input';
const SEARCH_INPUT_QUERY_PARAM = 'headerTextQuery';
const TEST_ENV_HOST = "www.r1s-test.com";
// eslint-disable-next-line no-restricted-globals
const CURRENT_ENV = location.host;

export class NavbarContainer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            searchInputFocused: false,
            showMobileMenu: false,
            showTestEnvBanner: CURRENT_ENV === TEST_ENV_HOST,
            scrollPosition: 0
        };

        this.generateHeaderHeight = throttle(this.generateHeaderHeight.bind(this), 100);
        this.onSearchInputFocus = this.onSearchInputFocus.bind(this);
        this.onSearchInputBlur = this.onSearchInputBlur.bind(this);
        this.clickedMobileMenuButton = throttle(this.clickedMobileMenuButton.bind(this), 300);
        this.watchForScroll = debounce(this.watchForScroll, 300, { 'trailing': true });

        // Logout Guest users if not on any allowed Guest route
        if (securityHelper.isGuest() && !GUEST_LOGIN_ALLOWED_PATH.test(window.location.pathname)) {
            this.props.logout();
        }
    }

    componentDidMount() {
        window.addEventListener('resize', this.generateHeaderHeight);
        window.addEventListener('scroll', this.watchForScroll);
        this.generateHeaderHeight();

        // Add event listener to focus events to ensure Navbar doesn't visually block
        window.addEventListener('keyup', this.ensureNonblockingFocus);

        this.props.updateAccountServiceStatus();

        if (securityHelper.isLoggedIn()) {
            this.props.loggedIn(securityHelper.getRecAccount());
        }
        else if (securityHelper.isGuest()) {
            this.props.loggedIn(securityHelper.getGuestAccount());
        }

        recaptchav3Lib.loadScript(process.env.RECAPTCHAV3_SITE_KEY);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.generateHeaderHeight);
        window.removeEventListener('scroll', this.watchForScroll);
        window.removeEventListener('keyup', this.ensureNonblockingFocus);
    }

    onSearchInputFocus() {
        this.setState({
            searchInputFocused: true
        });
    }

    onSearchInputBlur() {
        this.setState({
            searchInputFocused: false
        });
    }

    ensureNonblockingFocus = (e) => {
        // If the user hits the tab key and the target element
        // is not a child of the NavbarContainer
        if (
            window.scrollY > 0
            && (e.code === 'Tab' || e.keyCode === 9)
            && !(document.getElementById("nav-header-container").contains(e.target))
        ) {
            // Grab the top of the newly focused element
            const topOffset = e.target.getBoundingClientRect().top;

            // If the top is less than 84 pixels, it or its outline
            // is being blocked by the floating header (79px / up to 5px, respectively)
            if (topOffset < 84) {
                // Grab the main div and get its current top
                const mainDiv = document.getElementById("recApp");
                const mainOffset = mainDiv.getBoundingClientRect().top;

                // Scroll to the existing location on the page,
                // but showing the newly focused element
                window.scrollTo(0, (-1 * mainOffset) + (topOffset - 84));
            }
        }
    }

    generateHeaderHeight() {
        // We need to set the height of the placeholder that enables the sticky header.
        // As we have multiple potential banners, we need to get the height of each rendered
        // banner and add it to the base height of the Navbar.
        let navHeaderHeight = window.innerWidth >= 768 ? 79 : 73;

        if (this.state.showTestEnvBanner) {
            const testBannerHeight = this.testEnvBanner.clientHeight;

            navHeaderHeight += testBannerHeight;
            if (this.navHeader) {
                this.navHeader.style.marginTop = `${testBannerHeight}px`;
            }
        }

        if (this.navHeaderPlaceholder) {
            this.navHeaderPlaceholder.style.height = `${navHeaderHeight}px`;
        }
    }

    isSearchTermPresent(props) {
        return props.search[SEARCH_INPUT_QUERY_PARAM] && props.search[SEARCH_INPUT_QUERY_PARAM].length > 0;
    }

    clickedMobileMenuButton() {
        this.setState({
            showMobileMenu: !this.state.showMobileMenu
        });
    }

    watchForScroll = () => {
        const scrolled = window.pageYOffset;

        if ((this.state.scrollPosition === 0 && scrolled > 0) || (this.state.scrollPosition > 0 && scrolled === 0)) {
            this.setState({ scrollPosition: scrolled });
        }
    }

    render() {
        const { scrollPosition } = this.state;
        const hasShadow = scrollPosition === 0 ? '' : 'has-shadow';

        // don't show header in native mobile app
        if (securityHelper.isMobileApp()) {
            return (<MobileAppNavbar />);
        }

        let mobileNavActiveClass = "";
        if (this.state.showMobileMenu) {
            mobileNavActiveClass = "nav-active";
        }

        let commercialAccountNotice = null;
        if (this.props.auth.account
            && this.props.auth.account.account
            && this.props.auth.account.account.is_commercial_account) {
            commercialAccountNotice = (
                <div className="commercial-account-title">
                    Commercial
                </div>
            )
        }

        let testEnvBanner = null;
        if (this.state.showTestEnvBanner) {
            testEnvBanner = (
                <div
                    className="nav-test-env-banner"
                    ref={(node) => this.testEnvBanner = node}>
                    Recreation.gov Test Environment
                </div>
            );
        }

        return (
            <React.Fragment>
                <div
                    className="nav-header-placeholder"
                    ref={(node) => {
                        this.navHeaderPlaceholder = node;
                    }} />
                <header
                    className={`nav-header-container sticky ${hasShadow}`}
                    id="nav-header-container">
                    <SkipNavLink />

                    <AndroidAppDownloadBanner />

                    {testEnvBanner}

                    <div
                        className="nav-header"
                        ref={(node) => this.navHeader = node}>
                        <div className="nav-header-left">
                            <div className="nav-logo-holder">
                                <a href="/" title="Recreation.gov Homepage" aria-label="Recreation.gov Homepage">
                                    <BrandingIcons.Logo />
                                </a>
                                {commercialAccountNotice}
                            </div>
                        </div>
                        <nav className="nav-header-right">
                            <div className="nav-interactions-holder">
                                <div className="nav-search-bar-wrap">
                                    <div className="nav-interactions-search-icon">
                                        <Icons.IconSearch alt="Search" aria-hidden="true" />
                                    </div>
                                    <div className="nav-search-input-wrapper">
                                        <SearchInputContainer
                                            ariaLabel="Top navigation search"
                                            queryCriteriaPropertyName={SEARCH_INPUT_QUERY_PARAM}
                                            onBlur={this.onSearchInputBlur}
                                            onFocus={this.onSearchInputFocus}
                                            searchInputID={NAVBAR_SEARCH_INPUT_ID} />
                                    </div>
                                </div>
                                <div className="nav-links-wrap">
                                    <Button
                                        appearance="link"
                                        href="https://recreationonestopprod.service-now.com/external"
                                        aria-label="Help"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="nav-link">
                                        Help
                                    </Button>
                                    <AccountPanel
                                        accountServiceEnabled={this.props.auth.accountServiceEnabled}
                                        cart={this.props.cart}
                                        account={this.props.auth.account}
                                        isGuest={this.props.auth.isGuest}
                                        verification={this.props.verification}
                                        loginNotice={this.props.auth.loginNotice}
                                        loginError={this.props.auth.loginError}
                                        lockout={this.props.auth.lockout}
                                        mfaRequired={this.props.auth.mfaRequired}
                                        fetchCart={this.props.fetchCart}
                                        guestLogin={this.props.guestLogin}
                                        login={this.props.login}
                                        loggedIn={this.props.loggedIn}
                                        logout={this.props.logout}
                                        showLoginModal={this.props.modal.showLoginModal}
                                        clearShowLoginModal={this.props.clearShowLoginModal}
                                        showSignInGuestModal={this.props.modal.showSignInGuestModal}
                                        clearShowSignInGuestModal={this.props.clearShowSignInGuestModal}
                                        showCreateAccountModal={this.props.modal.showCreateAccountModal}
                                        clearShowCreateAccountModal={this.props.modal.clearShowCreateAccountModal}
                                        showResetModal={this.props.modal.showResetModal}
                                        clearShowResetModal={this.props.modal.clearShowResetModal}
                                        extendTimer={this.props.extendTimer} />
                                </div>
                            </div>
                            <button
                                className={`nav-mobile-menu-button ${mobileNavActiveClass}`}
                                aria-label="Navigation Menu"
                                aria-owns={this.state.showMobileMenu ? 'nav-mobile-menu-id' : null}
                                aria-expanded={this.state.showMobileMenu}
                                onClick={this.clickedMobileMenuButton}>
                                {this.state.showMobileMenu ? <Icons.IconClose /> : <Icons.IconRecMenu />}
                            </button>
                            <MobileMenuWrapper
                                showMobileMenu={this.state.showMobileMenu}
                                clickedMobileMenuButton={this.clickedMobileMenuButton}
                                logout={this.props.logout}
                                cart={this.props.cart}
                                auth={this.props.auth} />
                        </nav>
                    </div>
                </header>

                <AlertsNotificationContainer autoFetchForLocationChange />
            </React.Fragment>
        );
    }
}

NavbarContainer.propTypes = propTypes;

export default connect(
    (state) => ({
        search: state.search,
        cart: state.cart,
        auth: state.auth,
        modal: state.modal,
        verification: state.recaptcha.verification
    }),
    (dispatch) => bindActionCreators(combinedActions, dispatch)
)(NavbarContainer);
